import {css} from 'aphrodite';
import React, {useMemo, useState, useCallback, useEffect} from 'react';

import {assert} from 'Asserts';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {ProcessFlowchartSchemaMapperConfigSpec} from 'processes/dao/ProcessSchemaDAO';
import {TaskSchemaStaticNodeData} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRenderer';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';

import {ProcessTaskSchemaGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {SchemaInfo} from '../FieldMappingConstants';
import buildSchemaMapperConfigFromTransitionData from './buildSchemaMapperConfigFromTransitionData';
import buildTransitionDataFromTaskDefinitions from './buildTransitionDataFromTaskDefinitions';
import {getTaskSchemaIdFromNodeIdentifier} from './flowchartIdentifierUtils';
import TransitionModalWithFlowchartEditorBody from './TransitionModalWithFlowchartEditorBody';
import {
  CustomMappingData,
  FlowchartStaticEdgeData,
  isCustomMappingDataEmpty,
  ProcessFlowchartEdgeType,
  TRANSITION_MODAL_WITH_FLOWCHART_EDITOR_STYLES,
  TransitionSource,
} from './transitionModalWithFlowchartEditorConstants';
import {getAvailableNodeSchemaInfos} from './transitionModalWithFlowchartEditorUtils';

export interface BaseTransitionModalWithFlowchartEditorProps {
  flowchartTaskSchema: ProcessTaskSchemaGraphQL;
  fromSchemaInfos: Array<SchemaInfo>;
  toSchemaInfo: SchemaInfo | null;
  schemaMapperConfig: ProcessFlowchartSchemaMapperConfigSpec | null;
  allSchemasById: SchemaById;
  availableTaskSchemas: Array<ProcessTaskSchemaGraphQL>;
  disabled?: boolean;
}

interface TransitionModalWithFlowchartEditorProps extends BaseTransitionModalWithFlowchartEditorProps {
  onRequestClose: () => void;
  onRequestSubmit: (schemaMapperConfig: ProcessFlowchartSchemaMapperConfigSpec | null | undefined) => void;
}

export default function TransitionModalWithFlowchartEditor(
  props: TransitionModalWithFlowchartEditorProps
): JSX.Element {
  const {
    sourceNodesStaticData,
    targetNodeStaticData,
    edgesData,
    customMappingDataByEdgeId: initialCustomMappingDataByEdgeId,
  } = useMemo((): {
    sourceNodesStaticData: Array<TaskSchemaStaticNodeData>;
    targetNodeStaticData: TaskSchemaStaticNodeData | null;
    edgesData: Array<FlowchartStaticEdgeData>;
    customMappingDataByEdgeId: Record<string, CustomMappingData>;
  } => {
    if (props.toSchemaInfo == null) {
      assert(props.fromSchemaInfos.length === 0, 'Expected empty fromSchemaInfos if toSchemaInfo is null');
      assert(props.schemaMapperConfig == null, 'Expected null schemaMapperConfig if toSchemaInfo is null');
      return {
        sourceNodesStaticData: [],
        targetNodeStaticData: null,
        edgesData: [],
        customMappingDataByEdgeId: {},
      };
    }
    const transitionData = buildTransitionDataFromTaskDefinitions({
      fromSchemaInfos: props.fromSchemaInfos,
      toSchemaInfo: props.toSchemaInfo,
      schemaMapperConfigSpec: props.schemaMapperConfig,
    });
    return transitionData;
  }, [props.fromSchemaInfos, props.toSchemaInfo, props.schemaMapperConfig]);

  const [mappingsHaveChanged, setMappingsHaveChanged] = useState(false);
  const [customMappingDataByEdgeId, setCustomMappingDataByEdgeId] = useState<
    Record<string, CustomMappingData>
  >(initialCustomMappingDataByEdgeId);

  const taskSchemasById: Record<string, ProcessTaskSchemaGraphQL> = useMemo(() => {
    const acc: Record<string, ProcessTaskSchemaGraphQL> = {};
    for (const [id, schema] of Object.entries(props.allSchemasById)) {
      if (APIUtil.isProcessTaskSchema(id) && schema != null) {
        acc[id] = schema as ProcessTaskSchemaGraphQL;
      }
    }
    return acc;
  }, [props.allSchemasById]);

  const [transitionData, setTransitionData] = useState({
    nodesData: targetNodeStaticData
      ? [...sourceNodesStaticData, targetNodeStaticData]
      : sourceNodesStaticData,
    edgesData,
  });

  const getCurrentTargetNode = useCallback((): TaskSchemaStaticNodeData | null => {
    // The target node may have changed since the last time the transition data was updated.
    const currentTargetNodeId = transitionData.edgesData.find(
      (edge) => edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    )?.target;
    if (currentTargetNodeId == null) {
      return null;
    }
    const targetNode = transitionData.nodesData.find(
      (node) => node != null && node.id === currentTargetNodeId
    );
    return targetNode ?? null;
  }, [transitionData.edgesData, transitionData.nodesData]);

  // TODO(BNCH-122422): This is a temporary solution to derive the transition source based on the task schemas
  // involved. However users will be able to configure the transition source manually. When they can do that,
  // we need to update this function to use the user-defined transition source and use this algorithm as the
  // default.
  const temporarilyGetTransitionSource = (): TransitionSource | null => {
    const currentTargetNode = getCurrentTargetNode();
    if (currentTargetNode == null) {
      return null;
    }
    const sourceTaskSchemas = transitionData.nodesData
      .filter((node) => node.id !== currentTargetNode.id)
      .map((node) => getTaskSchemaIdFromNodeIdentifier(node.id));
    if (
      sourceTaskSchemas.some(
        (taskSchemaId) => taskSchemasById[taskSchemaId].executionType === ProcessExecutionType.FLOWCHART
      )
    ) {
      return TransitionSource.INPUTS;
    } else if (sourceTaskSchemas.some((taskSchemaId) => taskSchemasById[taskSchemaId].outputSchema == null)) {
      return TransitionSource.INPUTS;
    }
    return TransitionSource.OUTPUTS;
  };

  const isSubmitDisabled = (): boolean => {
    const currentTargetNode = getCurrentTargetNode();
    if (currentTargetNode == null) {
      return true;
    }
    const transitionSource = temporarilyGetTransitionSource();
    if (transitionSource == null) {
      return true;
    }
    return false;
  };

  const transitionSource = temporarilyGetTransitionSource();
  const currentTargetNodeForBody = getCurrentTargetNode();
  const initialSourceNodesForBody = transitionData.nodesData.filter(
    (node): node is TaskSchemaStaticNodeData => {
      if (node == null) {
        return false;
      }
      if (currentTargetNodeForBody == null) {
        return true;
      }
      return node.id !== currentTargetNodeForBody.id;
    }
  );

  const [availableNodeSchemaInfosForSidepanel, setAvailableNodeSchemaInfosForSidepanel] = useState<
    Array<SchemaInfo>
  >(
    getAvailableNodeSchemaInfos(
      transitionData.nodesData,
      props.flowchartTaskSchema,
      props.availableTaskSchemas
    )
  );

  useEffect(() => {
    setAvailableNodeSchemaInfosForSidepanel(
      getAvailableNodeSchemaInfos(
        transitionData.nodesData,
        props.flowchartTaskSchema,
        props.availableTaskSchemas
      )
    );
  }, [transitionData.nodesData, props.flowchartTaskSchema, props.availableTaskSchemas]);

  return (
    <Modal
      title={props.disabled ? 'View mapping' : 'Configure mapping'}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        if (isSubmitDisabled()) {
          return;
        }
        const currentTargetNode = getCurrentTargetNode();
        assert(
          currentTargetNode != null && transitionSource != null,
          'Current target node and transition source must be non-null'
        );
        const schemaMapperConfig = buildSchemaMapperConfigFromTransitionData({
          destinationNode: currentTargetNode,
          edges: transitionData.edgesData,
          customMappingDataByEdgeId,
          transitionSource,
          allTaskSchemasById: taskSchemasById,
        });

        props.onRequestSubmit(schemaMapperConfig);
      }}
      buttons={
        mappingsHaveChanged
          ? [
              {
                key: 'finish',
                text: 'Finish',
                disabled: isSubmitDisabled(),
                submit: true,
              },
            ]
          : [
              {
                key: 'close',
                text: 'Close',
                submit: true,
                style: ButtonStyle.TERTIARY_GRAY,
              },
            ]
      }
      expandHeight={true}
      extraLarge={true}
      bodyPadded={false}
    >
      <div className={css(TRANSITION_MODAL_WITH_FLOWCHART_EDITOR_STYLES.editorBodyWrapper)}>
        <TransitionModalWithFlowchartEditorBody
          initialSourceNodesData={initialSourceNodesForBody}
          initialTargetNodeData={currentTargetNodeForBody}
          initialEdgesData={transitionData.edgesData}
          availableNodeSchemaInfos={availableNodeSchemaInfosForSidepanel}
          disabled={props.disabled}
          onChange={(newTransitionData) => {
            setTransitionData(newTransitionData);
            setMappingsHaveChanged(true);
          }}
          customMappingDataByEdgeId={customMappingDataByEdgeId}
          onChangeCustomMappingData={(edgeId, newCustomMappingData) => {
            if (isCustomMappingDataEmpty(newCustomMappingData)) {
              setCustomMappingDataByEdgeId((prev) => {
                const newCustomMappingDataByEdgeId = {...prev};
                delete newCustomMappingDataByEdgeId[edgeId];
                return newCustomMappingDataByEdgeId;
              });
            } else {
              setCustomMappingDataByEdgeId((prev) => ({...prev, [edgeId]: newCustomMappingData}));
            }
          }}
          flowchartTaskSchema={props.flowchartTaskSchema}
          allSchemasById={props.allSchemasById}
          transitionSource={transitionSource}
        />
      </div>
    </Modal>
  );
}
